import { useCallback, useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetOrganizationsQuery } from '@/entities/organizations';

import { useUrlState, useProjectSiteParams } from '@/shared/lib/hooks';
import { useDebounce } from '@/shared/lib/hooks/use-debounce';
import { compareFilters, isDefaultFilters } from '@/shared/lib/utils';
import type { DateRangeValue, SelectOption } from '@/shared/ui';

import type { ConsignmentsUrlState } from '../model/types';
import { DEFAULT_URL_STATE } from './constants';
import { buildConsignmentsFilterChips } from './build-filter-chips';
import { useAllSitesViewpoint } from './use-all-sites-viewpoint';

// `allSites` is the viewpoint switch in the header, not a panel filter, and `q`
// has its own search input — neither takes part in the panel's apply/reset flow.
const NON_PANEL_KEYS: (keyof ConsignmentsUrlState)[] = ['q', 'allSites'];

const PANEL_RESET_KEYS = (Object.keys(DEFAULT_URL_STATE) as (keyof ConsignmentsUrlState)[]).filter(
  (key) => key !== 'allSites'
);

export const useConsignmentsFilters = () => {
  const { t } = useTranslation();
  const { projectId } = useProjectSiteParams();
  const [urlState, setUrlState, resetUrlState] = useUrlState(DEFAULT_URL_STATE);
  const [organizationSearch, setOrganizationSearch] = useState('');
  const [localFilters, setLocalFilters] = useState<ConsignmentsUrlState>(() => urlState);
  const [searchQuery, setSearchQuery] = useState(() => urlState.q);

  const debouncedOrganizationSearch = useDebounce(organizationSearch, 300);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const { data: organizationsData, isLoading: isLoadingOrganizations } = useGetOrganizationsQuery(
    {
      search: debouncedOrganizationSearch || undefined,
      projectId
    },
    {
      skip: !projectId
    }
  );

  const organizationOptions: SelectOption[] = useMemo(() => {
    if (!organizationsData?.organizations) return [];

    return organizationsData.organizations.map((org) => ({
      value: org.id.toString(),
      label: org.name,
      keywords: org.abbreviation ? [org.abbreviation] : undefined
    }));
  }, [organizationsData]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;

    // Check date range filter (period, creationDateFrom, creationDateTo count as one filter)
    const hasDateFilter =
      (urlState.period && urlState.period !== DEFAULT_URL_STATE.period) ||
      (urlState.creationDateFrom && urlState.creationDateFrom !== '') ||
      (urlState.creationDateTo && urlState.creationDateTo !== '');

    if (hasDateFilter) {
      count++;
    }

    Object.entries(urlState).forEach(([key, value]) => {
      // Skip pagination, sorting, date-related fields and the header viewpoint
      if (
        key === 'size' ||
        key === 'sortField' ||
        key === 'sortDirection' ||
        key === 'period' ||
        key === 'creationDateFrom' ||
        key === 'creationDateTo' ||
        key === 'allSites'
      ) {
        return;
      }

      // Count non-empty values as active filters
      if (value && value !== '' && value !== DEFAULT_URL_STATE[key as keyof ConsignmentsUrlState]) {
        count++;
      }
    });

    return count;
  }, [urlState]);

  const hasActiveFilters = activeFiltersCount > 0;

  const isFiltersDirty = useMemo(() => {
    return !compareFilters(localFilters, urlState, NON_PANEL_KEYS);
  }, [localFilters, urlState]);

  const hasDefaultFilters = useMemo(() => {
    return isDefaultFilters(urlState, DEFAULT_URL_STATE, NON_PANEL_KEYS);
  }, [urlState]);

  const handleFilterChange = useCallback((key: string, value: string) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setSelectedOrganization = useCallback((value?: string) => {
    const actualValue = value || '';
    setLocalFilters((prev) => ({ ...prev, orgId: actualValue }));
  }, []);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleDateRangeChange = useCallback(({ fromDate, toDate }: DateRangeValue) => {
    setLocalFilters((prev) => ({
      ...prev,
      creationDateFrom: fromDate || '',
      creationDateTo: toDate || ''
    }));
  }, []);

  // Reset / "Clear all filters" covers the panel only — the header viewpoint
  // survives, so clearing filters never silently switches what is being listed.
  const handleReset = useCallback(() => {
    setLocalFilters((prev) => ({ ...DEFAULT_URL_STATE, allSites: prev.allSites }));
    resetUrlState(PANEL_RESET_KEYS);
  }, [resetUrlState]);

  // Remove a single applied filter directly (used by the active-filters row).
  // Writes straight to the committed URL state — no Apply step. The 'date'
  // key clears the whole date range (period + creationDateFrom/To).
  const removeFilter = useCallback(
    (key: string) => {
      if (key === 'date') {
        setUrlState({
          period: DEFAULT_URL_STATE.period,
          creationDateFrom: DEFAULT_URL_STATE.creationDateFrom,
          creationDateTo: DEFAULT_URL_STATE.creationDateTo
        });
        return;
      }
      setUrlState({ [key]: DEFAULT_URL_STATE[key as keyof ConsignmentsUrlState] });
    },
    [setUrlState]
  );

  const activeFilters = useMemo(
    () => buildConsignmentsFilterChips({ state: urlState, t, organizationOptions, onRemove: removeFilter }),
    [urlState, t, organizationOptions, removeFilter]
  );

  // The header toggle commits straight to the URL — no Apply step — and is the
  // only writer of the persisted viewpoint.
  const setAllSites = useCallback(
    (value: string) => {
      setUrlState({ allSites: value });
    },
    [setUrlState]
  );

  const persistAndSetAllSites = useAllSitesViewpoint({ allSites: urlState.allSites, onChange: setAllSites });

  const handleToggleAllSites = useCallback(
    (checked: boolean) => {
      persistAndSetAllSites(String(checked));
    },
    [persistAndSetAllSites]
  );

  const handleApplyFilters = useCallback(() => {
    const { q, allSites, ...panelFilters } = localFilters;
    setUrlState(panelFilters);
  }, [localFilters, setUrlState]);

  useEffect(() => {
    setLocalFilters(urlState);
  }, [urlState]);

  useEffect(() => {
    if (debouncedSearchQuery !== urlState.q) {
      setUrlState({ q: debouncedSearchQuery });
    }
  }, [debouncedSearchQuery, setUrlState, urlState.q]);

  return {
    // State
    urlState: localFilters,
    // Committed viewpoint — drives the header toggle and the page title
    allSites: urlState.allSites === 'true',
    activeFilters,
    searchQuery,
    activeFiltersCount,
    hasActiveFilters,
    isFiltersDirty,
    hasDefaultFilters,

    // Organization
    organizationOptions,
    isLoadingOrganizations,
    organizationSearch,
    setOrganizationSearch,
    setSelectedOrganization,

    // Handlers
    handleFilterChange,
    handleSearchChange,
    handleDateRangeChange,
    handleReset,
    handleToggleAllSites,
    handleApplyFilters
  };
};
